import {
  AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import { AuthConfig, loadAuthConfig } from './authConfig';
import { clearGeoServerTokenCookie, setGeoServerTokenCookie } from '@/utils/geoserverAccess';

/**
 * Sign-in through UMS (Veramenta docs/en/24 sec.11):
 *
 *   1. the person signs in at our identity provider (MSAL, auth code + PKCE);
 *   2. its token is exchanged at UMS for a GeoNexora token of one company
 *      (`POST /oauth2/token`, audiences `geonexora` and `ums`); somebody in several companies picks one;
 *   3. that token goes to the backend as before (`auth_token`, and the GeoServer cookie).
 *
 * UMS tokens live minutes, not days: the exchange is repeated, silently, before one ends.
 */

const TOKEN = 'auth_token';
const EXPIRES = 'token_expires_at';
const TENANT = 'ums_tenant';
const PRODUCT = 'geonexora';
const UMS_AUDIENCE = 'ums';

export interface CompanyChoice {
  id: string;
  slug: string;
  name: string;
}

export type SignInResult =
  | { state: 'signed-in'; tenant: string }
  | { state: 'choose-company'; companies: CompanyChoice[] }
  | { state: 'signed-out' }
  | { state: 'refused'; reason: string; message: string };

let msal: Promise<PublicClientApplication> | null = null;
let renewal: ReturnType<typeof setTimeout> | null = null;

/**
 * The one MSAL client, initialized. Kept as a promise: two callers at once (React runs the sign-in effect twice in
 * development) must both wait for the same initialize(), not get the client before it finished.
 */
function client(config: AuthConfig): Promise<PublicClientApplication> {
  if (!msal) {
    const app = new PublicClientApplication({
      auth: {
        clientId: config.clientId!,
        authority: config.authority!,
        knownAuthorities: config.knownAuthorities ?? [],
        redirectUri: config.redirectUri ?? `${window.location.origin}/login`,
        postLogoutRedirectUri: `${window.location.origin}/login`,
      },
      cache: { cacheLocation: 'localStorage' },
    });
    msal = app.initialize().then(() => app, (e) => {
      msal = null;
      throw e;
    });
  }
  return msal;
}

/** True when this deployment signs in through UMS. */
export async function usesUms(): Promise<boolean> {
  return (await loadAuthConfig()).mode === 'ums';
}

/** Sends the browser to the identity provider. */
export async function startSignIn(): Promise<void> {
  const config = await loadAuthConfig();
  const app = await client(config);
  await app.loginRedirect({ scopes: config.scopes ?? [], prompt: 'select_account' });
}

/** After the identity provider sent the browser back (or on any start): get a GeoNexora token. */
export async function completeSignIn(tenant?: string): Promise<SignInResult> {
  const config = await loadAuthConfig();
  const app = await client(config);
  const redirect = await app.handleRedirectPromise();
  const account: AccountInfo | undefined = redirect?.account ?? app.getAllAccounts()[0];
  if (!account) {
    return { state: 'signed-out' };
  }
  app.setActiveAccount(account);
  const idpToken = await identityToken(app, config, account);
  if (!idpToken) {
    return { state: 'signed-out' };
  }
  return exchange(config, idpToken, tenant ?? localStorage.getItem(TENANT) ?? undefined);
}

/** Another company of the same person. */
export async function switchCompany(tenant: string): Promise<SignInResult> {
  localStorage.setItem(TENANT, tenant);
  return completeSignIn(tenant);
}

/** Forget the company this browser signed in for and ask again: the list of the person's companies. */
export async function chooseCompanyAgain(): Promise<SignInResult> {
  localStorage.removeItem(TENANT);
  return completeSignIn();
}

/** Ends the GeoNexora session, and the identity provider's. */
export async function signOut(): Promise<void> {
  stopRenewal();
  localStorage.removeItem(TOKEN);
  localStorage.removeItem(EXPIRES);
  localStorage.removeItem(TENANT);
  clearGeoServerTokenCookie();
  const config = await loadAuthConfig();
  if (config.mode !== 'ums') {
    return;
  }
  const app = await client(config);
  const account = app.getActiveAccount() ?? app.getAllAccounts()[0];
  if (account) {
    await app.logoutRedirect({ account });
  }
}

/** Once more, silently: for when the backend refuses an expired token. */
let renewing: Promise<boolean> | null = null;

export async function renewNow(): Promise<boolean> {
  // Several requests refused at once (the map's images, the layer list, a report) share one renewal.
  if (!renewing) {
    renewing = completeSignIn()
      .then((result) => result.state === 'signed-in')
      .catch(() => false)
      .finally(() => {
        renewing = null;
      });
  }
  return renewing;
}

async function identityToken(app: PublicClientApplication, config: AuthConfig, account: AccountInfo): Promise<string | null> {
  try {
    const result = await app.acquireTokenSilent({ scopes: config.scopes ?? [], account });
    return result.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      return null;
    }
    throw e;
  }
}

async function exchange(config: AuthConfig, idpToken: string, tenant?: string): Promise<SignInResult> {
  const form = new URLSearchParams({
    grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
    subject_token: idpToken,
    subject_token_type: 'urn:ietf:params:oauth:token-type:access_token',
    audience: PRODUCT,
  });
  // The same token also opens GeoNexora's user management service (the Users screen and the company
  // admin's invitations), as Veramenta's back office does: UMS decides each call from its own grants.
  form.append('audience', UMS_AUDIENCE);
  if (tenant) {
    form.set('tenant', tenant);
  }
  const response = await fetch(`${config.umsUrl!.replace(/\/$/, '')}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: form.toString(),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (body.reason === 'tenant_selection_required' && Array.isArray(body.tenants)) {
      return { state: 'choose-company', companies: body.tenants as CompanyChoice[] };
    }
    if (tenant && body.reason === 'no_membership') {
      // The remembered company is no longer theirs: ask again.
      localStorage.removeItem(TENANT);
      return exchange(config, idpToken);
    }
    return { state: 'refused', reason: body.reason ?? 'unknown', message: body.error_description ?? '' };
  }
  const expiresAt = Date.now() + Number(body.expires_in ?? 300) * 1000;
  localStorage.setItem(TOKEN, body.access_token);
  localStorage.setItem(EXPIRES, String(expiresAt));
  localStorage.setItem(TENANT, String(body.tenant));
  setGeoServerTokenCookie(body.access_token, expiresAt);
  scheduleRenewal(expiresAt);
  return { state: 'signed-in', tenant: String(body.tenant) };
}

function scheduleRenewal(expiresAt: number): void {
  stopRenewal();
  // A minute before it ends, or at four fifths of its life for short tokens.
  const life = expiresAt - Date.now();
  const at = Math.max(5_000, Math.min(life - 60_000, life * 0.8));
  renewal = setTimeout(() => {
    void renewNow();
  }, at);
}

function stopRenewal(): void {
  if (renewal) {
    clearTimeout(renewal);
    renewal = null;
  }
}

/** The company the current token is for, as UMS knows it. */
export function currentTenant(): string | null {
  return localStorage.getItem(TENANT);
}
